import { useMemo } from "react";

import {
    AppButton,
    MarketHoursAlert,
    useBalanceChecker,
    useLoopMarketHoursAlertConfig,
    useLoopWindAlerts,
    useUserGeoblockedAssets
} from "app/components/common";
import { COPY } from "app/constants";
import { Column, TagTextAlert } from "@bridgesplit/ui";
import {
    BsMetaUtil,
    getLoopBorrowCapDetails,
    isValidLeverage,
    isWhirlpoolMetadata,
    useTokenListMetadataByMint,
    useAuth
} from "@bridgesplit/abf-react";

import { useLoopWind } from "./util";
import { getLoopInputMintFromForm, useWindContext } from "./WindContext";
import { useLoopContext } from "../LoopContext";
import WindRiskMessage from "./WindRiskMessage";

const MIN_COLLATERAL_USD = 1;
const ERR_PRICE_IMPACT_PCT = 0.02; // 2% price impact error

export default function WindCta() {
    const wind = useLoopWind();
    const { form, isQuoteFetching, quote, externalQuote, collateralAmount, leveragedCollateralAmount } =
        useWindContext();

    const { loopExpanded } = useLoopContext();
    const metadata = useTokenListMetadataByMint(form.inputToken?.mint);
    const isOrcaPosition = isWhirlpoolMetadata(metadata);

    const { insufficientBalance } = useBalanceChecker({
        mint: loopExpanded?.inputToken.mint,
        amount: form.inputAmount,
        skip: isOrcaPosition
    });
    const { windDisabledWithMessage } = useLoopWindAlerts(loopExpanded);
    const { isWalletBased, isAuthenticated } = useAuth();
    const { isClosed: disabledMarketHours, alertConfig: marketHoursAlertConfig } =
        useLoopMarketHoursAlertConfig(loopExpanded);

    const { isBlocked: getAssetBlocked } = useUserGeoblockedAssets();

    const disabledWithGeoMessage = useMemo(() => {
        if (!loopExpanded) {
            return undefined;
        }
        if (getAssetBlocked(loopExpanded?.collateralToken.mint)) {
            return `${loopExpanded?.collateralToken.symbol} is not available in your country`;
        }
        if (getAssetBlocked(loopExpanded?.principalToken.mint)) {
            return `${loopExpanded?.principalToken.symbol} is not available in your country`;
        }
        return undefined;
    }, [getAssetBlocked, loopExpanded]);

    const borrowCapDetails = useMemo(() => {
        if (!loopExpanded) return undefined;
        return getLoopBorrowCapDetails(loopExpanded);
    }, [loopExpanded]);

    const ctaError = useMemo(() => {
        if (disabledMarketHours && !disabledWithGeoMessage) return "Market closed";
        if (!isValidLeverage(loopExpanded?.loopVault?.maxLeverage)) return "No leverage available";
        if (!collateralAmount) return "Enter an amount";
        if (!form.inputAmount) return "Enter an amount";
        if (!isValidLeverage(form.multiplier)) return `Enter ${COPY.LEVERAGE_TERM.toLowerCase()}`;
        if (isQuoteFetching) return "Fetching";
        return undefined;
    }, [
        form.inputAmount,
        form.multiplier,
        isQuoteFetching,
        loopExpanded?.loopVault?.maxLeverage,
        collateralAmount,
        disabledMarketHours,
        disabledWithGeoMessage
    ]);

    const alertInfo = useMemo(() => {
        if (!disabledMarketHours && !disabledWithGeoMessage && marketHoursAlertConfig)
            return <MarketHoursAlert config={marketHoursAlertConfig} isError={false} />;
    }, [disabledMarketHours, marketHoursAlertConfig, disabledWithGeoMessage]);

    const alertError = useMemo(() => {
        if (disabledWithGeoMessage) return disabledWithGeoMessage;
        if (disabledMarketHours)
            return <MarketHoursAlert config={marketHoursAlertConfig} isError={disabledMarketHours} />;
        if (isQuoteFetching) return undefined;
        if (!isWalletBased) return "No wallet connected";
        if (borrowCapDetails?.remainingGlobalCapacity === 0)
            return `${BsMetaUtil.getSymbol(loopExpanded?.principalToken)} borrow cap exceeded`;
        if (windDisabledWithMessage) return windDisabledWithMessage;

        if (!isQuoteFetching && collateralAmount && form.multiplier) {
            if (quote === null) return "No matching liquidity. Please try lower deposit amount or leverage";
            if (!externalQuote && isAuthenticated) return "Not enough liquidity available on Jupiter";
        }
        const collateralUsd = loopExpanded?.inputTokenOracle.getUsdAmount(form.inputAmount);
        if (collateralUsd && collateralUsd < MIN_COLLATERAL_USD)
            return `Positions must be at least $${MIN_COLLATERAL_USD}`;

        if (
            leveragedCollateralAmount &&
            loopExpanded?.maxCollateralAmount &&
            leveragedCollateralAmount > loopExpanded?.maxCollateralAmount
        )
            return `Exceeds max ${BsMetaUtil.getSymbol(loopExpanded?.collateralToken)} amount`;

        if (borrowCapDetails && quote?.principalAmount) {
            const { exceedsGlobalCap, exceedsPerLoanCap } = borrowCapDetails;

            if (exceedsPerLoanCap(quote.principalAmount)) return "Exceeds borrow limit";
            if (exceedsGlobalCap(quote.principalAmount)) return "Exceeds borrow cap";
        }
        if (externalQuote?.priceImpactPct && externalQuote.priceImpactPct > ERR_PRICE_IMPACT_PCT)
            return `Exceeds price impact limit`;
        if (insufficientBalance) return "Insufficient balance";

        return undefined;
    }, [
        disabledMarketHours,
        marketHoursAlertConfig,
        isQuoteFetching,
        borrowCapDetails,
        windDisabledWithMessage,
        quote,
        insufficientBalance,
        isWalletBased,
        externalQuote,
        isAuthenticated,
        collateralAmount,
        form,
        loopExpanded,
        leveragedCollateralAmount,
        disabledWithGeoMessage
    ]);

    return (
        <Column spacing={1.5}>
            <AppButton
                isTransaction
                asyncCta={{
                    onClickWithResult: () =>
                        wind({
                            collateralMint: externalQuote?.outputMint,
                            inputMint: getLoopInputMintFromForm(form)
                        }),
                    options: { alertOnError: true }
                }}
                color="secondary"
                disabled={!!alertError || !!ctaError}
            >
                {ctaError || COPY.LOOP_TERM_ACTION}
            </AppButton>
            <WindRiskMessage />
            {alertError && (
                <TagTextAlert color="warning" textSx={{ display: "inline-block" }} icon="warning">
                    {alertError}
                </TagTextAlert>
            )}
            {alertInfo && (
                <TagTextAlert color="secondary" textSx={{ display: "inline-block" }} icon="tooltip">
                    {alertInfo}
                </TagTextAlert>
            )}
        </Column>
    );
}
